<template>
  <ion-page v-theme-scope="themeScope">
    <ion-content ref="content" :fullscreen="true">
      <!--
        Spec 6.7, screen 3. What an approved device shows when another one is
        waiting.

        This is the moment the member IS the authority: the server cannot grant
        approval, it can only check a signature made with a key it never sees
        (decision 23). So the screen's whole job is to give them enough to
        recognise their own phone, and one plain sentence for when they do not.

        It performs nothing. Approve and Deny emit the device id; wrapping MK to
        that device's public key, signing the operation and calling the API
        belong to the caller, because MK must never come near a component.

        SINCE PHASE 2b (#1693) Approve is disabled until the member has read the
        other phone's code: `ApprovalCheck.vue` below offers the scan, the typed
        form and — behind a confirm — the explicit older-version fallback, and
        `check.state` says which of those has succeeded. DENY IS NOT GATED ON
        ANY OF IT: a member who cannot verify must still be able to refuse, and
        gating refusal on a verification would strand exactly the member this
        screen exists to protect.

        The push, so the member does not have to open the app at all, is api#1123
        and has no implementation yet — the modal that App.vue keeps on screen is
        still the whole of the notification.
      -->
      <!--
        THE CODE FIELD TAKES THE SCREEN WHILE IT HAS FOCUS. Two attempts at
        scrolling a bare <input> clear of the iOS keyboard inside this modal
        (the field mixin, then the note editor's own arithmetic) moved nothing
        on the device (2026-09-11). So while the member is typing, the device
        card and the warning above the field are hidden and the field sits
        directly under the heading — well above any keyboard — and they come
        back the moment focus leaves. The padding and scroll below stay as a
        second measure for the rare layout where the field still sits low.
      -->
      <div
        class="flex min-h-full flex-col px-6 pb-10 pt-24 transition-colors duration-300 bg-[var(--courses-surface)]"
        :style="keyboardPadStyle"
        @focusin="onFieldFocusIn"
        @focusout="onFieldFocusOut"
      >
        <div class="flex flex-1 flex-col items-start">
          <!-- A modal, so this CLOSES rather than navigating: there is nothing
               behind it to go back to. The request is not answered by closing —
               it stays pending and the poll brings the modal back — which is
               why it is a Close and not a Cancel. Never disabled by `busy`, for
               the reason Deny is not: a member must always be able to leave. -->
          <button
            type="button"
            data-testid="request-close"
            class="mb-6 text-[0.9375rem] font-bold underline text-[var(--courses-ink-muted)]"
            @click="$emit('close')"
          >
            {{ COPY.close }}
          </button>
          <h1
            class="text-[2.25rem] font-black leading-[2.5rem] tracking-[-0.03em] transition-colors duration-300 text-[var(--courses-ink)]"
          >
            {{ COPY.heading }}
          </h1>

          <!--
            `device` can be null for a frame: a Deny that lands between a render
            and the next poll empties the list, and a blank card with two live
            buttons on it is worse than nothing.
          -->
          <template v-if="device">
            <div
              v-show="!codeFocused"
              data-testid="request-device-card"
              class="mt-8 flex w-full flex-col rounded-2xl border border-[var(--courses-border)] bg-[var(--courses-surface-raised)] px-4 py-4"
            >
              <span class="text-[1.125rem] font-bold text-[var(--courses-ink)]">
                {{ device.name }}
              </span>
              <span class="mt-1 text-[0.9375rem] text-[var(--courses-ink-muted)]">
                {{ platformLabel(device.platform) }}
              </span>
              <span class="mt-1 text-[0.8125rem] text-[var(--courses-ink-subtle)]">
                {{ requestedAtLabel(device.createdAt) }}
              </span>
            </div>

            <p
              v-show="!codeFocused"
              class="mt-6 text-[1.0625rem] leading-[1.5rem] text-[var(--courses-ink)]"
            >
              {{ COPY.warning }}
            </p>

            <!--
              The check that decides whether Approve is live. It performs
              nothing: every event goes to the caller, which runs
              `approval-scan.js` and hands the answer back as `check`.
            -->
            <ApprovalCheck
              :state="checkState"
              :scan-available="scanAvailable"
              @scan="$emit('scan')"
              @submit-code="$emit('submit-code', $event)"
              @older-version="$emit('older-version')"
              @reset="$emit('reset')"
            />

            <!--
              A failure the member can act on says so and offers the button; one
              they cannot still says what happened, because a screen that fails
              silently is how a member ends up tapping Approve four times and
              spending four nonces. `retryable` is the store's, not a guess
              here: a stale challenge is retryable, a rejected signature is not.

              The sentence comes from `error.code`, never from `error.message`:
              the message is the API's own developer diagnostic ("Device is
              APPROVED, not PENDING"), and this is the screen where a member
              decides whether to trust a device. `role="alert"` because the box
              appears with no action from them — the approve they tapped came
              back — so a screen reader has to be told.
            -->
            <div
              v-if="error"
              data-testid="request-error"
              role="alert"
              class="mt-6 flex w-full flex-col items-start gap-2 rounded-2xl bg-[var(--courses-surface-sunken)] px-4 py-3"
            >
              <p class="text-[0.9375rem] text-[var(--courses-ink)]">{{ errorMessage }}</p>
              <button
                v-if="error.retryable"
                type="button"
                class="text-[0.9375rem] font-bold underline text-[var(--courses-ink)]"
                @click="$emit('retry')"
              >
                {{ COPY.retry }}
              </button>
            </div>
          </template>
        </div>

        <div
          v-if="device"
          class="flex w-full max-w-[600px] flex-col items-center gap-3 self-center pt-10"
        >
          <!--
            Both disabled while the caller works: approving is a challenge, a
            signature and a wrapping, several hundred milliseconds in which a
            second tap would spend a second nonce and fail the first call.

            And Approve is disabled until the code has been read (#1693).
            `approveEnabled` is the whole of that gate: a matched scan, an
            accepted typed code, or the member's own explicit choice to approve
            a phone too old to show one. Everything else — including the
            refusal — leaves it dead.
          -->
          <button
            type="button"
            data-testid="approve-device"
            class="w-full rounded-full bg-[var(--courses-ink)] py-4 text-[1.0625rem] font-extrabold text-[var(--courses-surface)] disabled:opacity-50"
            :disabled="busy || !approveEnabled"
            @click="$emit('approve', device.id)"
          >
            {{ COPY.approve }}
          </button>
          <!--
            ⚠️ `busy` ONLY. Deny must never wait on a verification: the member
            who cannot get a code to read is precisely the one who may be
            looking at a request they did not make, and a Deny they cannot reach
            leaves the request PENDING for the next launch to ask again.
          -->
          <button
            type="button"
            data-testid="deny-device"
            class="w-full rounded-full border-[1.5px] border-[var(--courses-border)] bg-[var(--courses-surface-raised)] py-4 text-[1.0625rem] font-extrabold text-[var(--courses-danger)] disabled:opacity-50"
            :disabled="busy"
            @click="$emit('deny', device.id)"
          >
            {{ COPY.deny }}
          </button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage } from '@ionic/vue';
import coursesTheme from '@/mixins/CoursesTheme';
import keyboardOverlap from '@/mixins/keyboard-overlap';
import privateRegion from '@/mixins/PrivateRegion';
import ApprovalCheck from '@/pages/courses/encryption/ApprovalCheck.vue';
import { platformLabel, requestedAtLabel } from '@/pages/courses/encryption/device-display';
import { errorCopy } from '@/pages/courses/encryption/error-copy';
import { APPROVAL_CHECK } from '@/utils/courses-encryption/approval-scan';
import { ENCRYPTION_ERROR_CODES } from '@/utils/courses-encryption-api';

/**
 * The three states that make Approve live (#1693): a scan whose device id AND
 * public key both matched, a typed code that parsed, and the member's own
 * explicit "this phone is on an older version". Everything else — nothing read
 * yet, a scan in flight, a refusal, a wrong phone, junk, a cancelled scan, no
 * camera — leaves it disabled.
 */
const APPROVE_ENABLING = Object.freeze([
  APPROVAL_CHECK.MATCHED,
  APPROVAL_CHECK.TYPED,
  APPROVAL_CHECK.OLDER_VERSION,
]);

/** Spec 6.7, screen 3. */
export const COPY = {
  heading: 'New device request',
  warning: "If this isn't you, tap Deny",
  approve: 'Approve',
  // Not "Cancel": closing answers nothing. The request stays pending and the
  // poll brings this back.
  close: 'Close',
  deny: 'Deny',
  retry: 'Try again',
  /**
   * This screen's own sentences for two codes the shared table has to keep
   * general: here a 409 and a 404 can only mean the request was answered
   * somewhere else, where the same codes on a registration or a recovery write
   * mean something quite different.
   */
  errors: {
    [ENCRYPTION_ERROR_CODES.CONFLICT]: 'That request has already been answered on another device.',
    [ENCRYPTION_ERROR_CODES.NOT_FOUND]:
      'That request is no longer there. It may already have been answered.',
  },
};

// Room past the keyboard, and the gap kept between the field and its top —
// the note editor's numbers (AddNoteDialog.vue).
const KEYBOARD_PAD_EXTRA_PX = 40;
const KEYBOARD_CLEARANCE_PX = 12;
// How long the geometry must hold still before the field is scrolled to; the
// Android 16+ pan animates for ~900ms and the iOS keyboard for ~250ms.
const KEYBOARD_SETTLE_MS = 200;

export default {
  name: 'CoursesNewDeviceRequest',
  components: { IonPage, IonContent, ApprovalCheck },
  mixins: [privateRegion, coursesTheme, keyboardOverlap],
  props: {
    /** One PENDING device from `courses-devices/pendingDevices`, or null. */
    device: { type: Object, default: null },
    /** True while the caller is wrapping, signing and calling. */
    busy: { type: Boolean, default: false },
    /**
     * `courses-devices` state.error: `{ code, message, retryable }`, or null.
     * Only `code` and `retryable` are read — `message` is the API's wire text
     * and belongs in a log, not on this screen.
     */
    error: { type: Object, default: null },
    /**
     * What the caller's out-of-band check is doing, as `approval-scan.js` names
     * it: `{ state }`, one of `APPROVAL_CHECK`. An object rather than a bare
     * string so the secret's shape can grow without another prop, and so a
     * caller that passes nothing gets IDLE — Approve disabled, which is the
     * safe default for a screen whose whole job is a decision.
     */
    check: { type: Object, default: () => ({ state: APPROVAL_CHECK.IDLE }) },
    /** False on a build with no camera; the child then offers only the typed form. */
    scanAvailable: { type: Boolean, default: false },
  },
  emits: ['approve', 'close', 'deny', 'retry', 'scan', 'submit-code', 'older-version', 'reset'],
  watch: {
    keyboardOffsetPx() {
      this.scheduleFieldScroll();
    },
  },
  beforeUnmount() {
    clearTimeout(this.fieldScrollTimer);
  },
  data() {
    return {
      /** The typed-code field has focus; see the template note on the keyboard. */
      codeFocused: false,
    };
  },
  computed: {
    COPY: () => COPY,
    keyboardPadStyle() {
      return this.keyboardOffsetPx
        ? `padding-bottom: ${this.keyboardOffsetPx + KEYBOARD_PAD_EXTRA_PX}px`
        : '';
    },
    /**
     * The caller's state, or IDLE. The `?.` is not defensive dressing: a prop
     * default only fills in for `undefined`, so a host that passes an explicit
     * `null` while a status read is in flight would otherwise throw in the
     * template — on the screen a member is standing in front of, mid-decision.
     */
    checkState() {
      return this.check?.state ?? APPROVAL_CHECK.IDLE;
    },
    /**
     * Whether the member has read the other phone's code. The gate lives here
     * rather than in the caller so a host that forgets to pass `check` gets the
     * safe answer, and so the rule is one line a reviewer can read.
     */
    approveEnabled() {
      return APPROVE_ENABLING.includes(this.checkState);
    },
    /** One sentence written for a member, chosen by `error.code`. */
    errorMessage() {
      return errorCopy(this.error, COPY.errors);
    },
  },
  methods: {
    platformLabel,
    requestedAtLabel,
    isCodeField(target) {
      return target instanceof HTMLElement && target.dataset?.testid === 'approval-code-input';
    },
    onFieldFocusIn(event) {
      if (this.isCodeField(event?.target)) this.codeFocused = true;
      this.scheduleFieldScroll();
    },
    onFieldFocusOut(event) {
      if (this.isCodeField(event?.target)) this.codeFocused = false;
    },
    scheduleFieldScroll() {
      clearTimeout(this.fieldScrollTimer);
      this.fieldScrollTimer = setTimeout(
        () => this.scrollFieldClearOfKeyboard(),
        KEYBOARD_SETTLE_MS,
      );
    },
    /** The note editor's arithmetic (AddNoteDialog.vue): the overlap, not the reported height. */
    async scrollFieldClearOfKeyboard() {
      if (!this.keyboardOffsetPx) return;
      const field = document.activeElement;
      if (!(field instanceof HTMLElement) || !this.$el?.contains?.(field)) return;
      const content = this.$refs.content?.$el;
      if (typeof content?.getScrollElement !== 'function') return;
      const scroller = await content.getScrollElement();
      if (!scroller || document.activeElement !== field) return;
      const scrollerRect = scroller.getBoundingClientRect();
      const rect = field.getBoundingClientRect();
      const visibleBottom = scrollerRect.bottom - this.keyboardOffsetPx - KEYBOARD_CLEARANCE_PX;
      if (rect.bottom <= visibleBottom) return;
      scroller.scrollTo({
        top: scroller.scrollTop + (rect.bottom - visibleBottom),
        behavior: 'auto',
      });
    },
  },
};
</script>
